"use client";

import ProductLink from "@/components/navigation/ProductLink";
import type { Product } from "@/types";
import { useLocale, useTranslations } from "next-intl";
import { getDeliveryLabel } from "@/lib/utils/delivery-label";
import { useSearchParams } from "next/navigation";
import { translateCityNames, translateCityName } from "@/config/locations";
import { PriceDisplay } from "@/components/PriceDisplay";
import { CATEGORIES, getCategoryName } from "@/config/categories";
import MobileImageCarousel from "./MobileImageCarousel";
import type { Locale } from "@/i18n/routing";

interface MobileProductCardProps {
  product: Product;
  onHover?: (id: string | number | null) => void;
  isUnavailable?: boolean;
}

export default function MobileProductCard({
  product,
  onHover,
  isUnavailable,
}: MobileProductCardProps) {
  const locale = useLocale();
  const localeKey = locale as Locale;
  const t = useTranslations("search");
  const searchParams = useSearchParams();

  const imageUrl = product.images?.[0] || "/placeholder.svg";
  const images = product.images?.length > 0 ? product.images : [imageUrl];

  // 지점 위치 번역
  const translatedBranchLocations =
    product.branch_locations && product.branch_locations.length > 0
      ? translateCityNames(product.branch_locations, localeKey)
      : [];

  const translatedPrimaryCity = product.primary_city
    ? translateCityName(product.primary_city, localeKey)
    : null;

  const locationDisplay =
    translatedBranchLocations.length > 0
      ? translatedBranchLocations.join(", ")
      : translatedPrimaryCity || null;

  // 카테고리 정보
  const categoryItem = product.category_code
    ? CATEGORIES.find((c) => c.code === product.category_code)
    : null;

  const categoryName = categoryItem
    ? getCategoryName(categoryItem, localeKey)
    : null;

  // 이용 방식 배지
  const isDeliveryOnly = product.is_delivery_only ?? false;
  const usageMethods = product.usage_methods ?? [];
  const hasPickup = usageMethods.some(
    (m) => m.method_type === "pickup" && m.enabled,
  );
  const hasDelivery = usageMethods.some(
    (m) => m.method_type === "delivery" && m.enabled,
  );
  const hasShipping = usageMethods.some(
    (m) => m.method_type === "shipping" && m.enabled,
  );

  const pricePerDay = product.price_per_day || 0;

  // 날짜 필터가 있으면 상세 페이지로 전달
  const checkin = searchParams.get("checkin");
  const checkout = searchParams.get("checkout");
  const dateQuery =
    checkin && checkout ? `?checkin=${checkin}&checkout=${checkout}` : "";

  return (
    <div
      className="py-5"
      onMouseEnter={() => onHover?.(product.id)}
      onMouseLeave={() => onHover?.(null)}
    >
      <ProductLink
        href={`/${locale}/products/${product.id}${dateQuery}`}
        className="block space-y-3"
      >
        {/* 상단: 상품명 + 위치·카테고리 */}
        <div className="space-y-1 px-4">
          <h3 className="text-lg font-semibold text-gray-900 line-clamp-2 leading-snug">
            {product.name}
          </h3>

          <div className="flex items-center gap-1 text-xs text-gray-500">
            {locationDisplay && (
              <span className="truncate">{locationDisplay}</span>
            )}
            {locationDisplay && categoryName && (
              <span className="text-gray-300">·</span>
            )}
            {categoryName && <span>{categoryName}</span>}
          </div>
        </div>

        {/* 이미지 캐러셀 — 풀폭 (패딩 없음) */}
        <div className="relative">
          <MobileImageCarousel images={images} productName={product.name} />
        </div>

        {/* 하단: 가격 + 이용방식 배지 */}
        <div className="space-y-2 px-4">
          {/* 가격 / 예약마감 */}
          <div
            className="flex items-baseline gap-1"
            style={{ fontFamily: '"Pretendard Variable", sans-serif' }}
          >
            {isUnavailable ? (
              <span className="text-[16px] font-bold text-gray-600">
                {t("availability.soldOut")}
              </span>
            ) : (
              <span className="text-[16px] font-bold text-gray-900">
                <PriceDisplay
                  price={pricePerDay}
                  sourceCurrency={product.currency}
                  serverPrice={product.convertedPricePerDay}
                  serverCurrency={product.displayCurrency}
                  size="md"
                  showCurrencyCode
                  className="text-[16px]!"
                />
                ~
              </span>
            )}
          </div>

          {/* 이용 방식 배지 — 회색 통일 */}
          <div className="flex items-center gap-1.5 flex-wrap text-xs text-gray-600 font-medium">
            {isDeliveryOnly ? (
              <span className="px-2 py-0.5 bg-gray-100 rounded-md">
                {t("shippingOnly.badge")}
              </span>
            ) : (
              <>
                {hasPickup && (
                  <span className="px-2 py-0.5 bg-gray-100 rounded-md">
                    {t("usageMethod.pickup")}
                  </span>
                )}
                {hasDelivery && (
                  <span className="px-2 py-0.5 bg-gray-100 rounded-md">
                    {getDeliveryLabel(product.partner_country_code, locale, t, {
                      fallbackKey: "usageMethod.delivery",
                      templateKey: "usageMethod.deliveryTemplate",
                    })}
                  </span>
                )}
                {hasShipping && (
                  <span className="px-2 py-0.5 bg-gray-100 rounded-md">
                    {t("usageMethod.shipping")}
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      </ProductLink>
    </div>
  );
}
